import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { OrderType } from '@models/order/order-type';
import { PlatformRequest } from '@models/order/platform/request';
import { TypeResponse, TypeResponseApi } from '@models/order/type/response';
import { PlatformResponse } from '@models/order/platform/response';
import { addNotification } from '../notification';
import { translate } from '@components/i18n';
import { ThunkCallback } from '../model/thunk-callback';

export const fetchAvailablePlatformsByDivision = createAsyncThunk<
    PlatformResponse[],
    string
>('select-type/fetchAvailablePlatformsByDivision', async (req, thunkAPI) => {
    try {
        const response = await axios.get<TypeResponseApi[]>(
            '/filial/bydivisao?divisao=' + req.replace('undefined', ''),
        );

        const { status, data } = response;

        if (status === 200) {
            const result = response.data.map((u) =>
                Object.assign(new TypeResponseApi(), u),
            );

            return result.map((u) => u.toTypeResponse());
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});


type TChangeBranch = {
    codigoFilial: number
    pedidoId: number
}

export const transferRequestChangeBranch = createAsyncThunk<void, TChangeBranch>(
    'transfer-select-type/change-branch', async (req, thunkAPI) => {
        try {
            const response = await axios.put<any>(`/pedidotransfer/validar/${req.pedidoId}/${req.codigoFilial}`);

            const { status, data } = response;

            if (status !== 200) {
                return thunkAPI.rejectWithValue(data);
            }
        } catch (e) {
            let message = e?.response?.data?.error?.message

            if(e?.response?.data?.length > 0){
                message = e.response.data[0]?.msgUsuario
            }

            thunkAPI.dispatch(
                addNotification({
                    type: 'error',
                    message: message,
                    title: translate('general.errorT'),
                }),
            );

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const transferRequestVerifyChangeBranch = createAsyncThunk<void, ThunkCallback<TChangeBranch>>(
    'transfer-select-type/verify-change-branch', async (req, thunkAPI) => {
        try {
            const response = await axios.put<any>(`/pedidotransfer/validar/${req.data.pedidoId}/${req.data.codigoFilial}`);

            const { status, data } = response;

            if (status !== 200) {
                req.onError()
                return thunkAPI.rejectWithValue(data);
            }
            req.onSuccess()
        } catch (e) {
            let message = e?.response?.data?.error?.message

            if(e?.response?.data?.length > 0){
                message = e.response.data[0]?.msgUsuario
            }

            req.onError(message)

            return thunkAPI.rejectWithValue(message);
        }
    },
);

interface IOrderTypeSelectorState {
    orderType: OrderType;
    availableTypes: TypeResponse[];
    availablePlatforms: PlatformResponse[];
    allAvailablePlatforms: PlatformResponse[];
    availablePlatformsByDivision: PlatformResponse[];
    request: PlatformRequest;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: string;

    selectedPlatform?: PlatformResponse

    isFetchingPlatform: boolean;
    isSuccessPlatform: boolean;
    isErrorPlatform: boolean;
    errorMessagePlatform: string;
}

const initialState: IOrderTypeSelectorState = {
    allAvailablePlatforms: [],
    orderType: undefined,
    availableTypes: [],
    availablePlatforms: [],
    availablePlatformsByDivision: [],
    request: {},
    isFetching: true,
    isSuccess: false,
    isError: false,
    isFetchingPlatform: true,
    isSuccessPlatform: false,
    isErrorPlatform: false,
    errorMessage: '',
    errorMessagePlatform: '',
};

const selectTypeSlice = createSlice({
    name: 'transfer-type-slice',
    initialState,
    reducers: {
        selectType: (state, action: PayloadAction<PlatformRequest>) => {
            const date = action.payload.date;
            const platform = state.availablePlatforms.find(
                (i) => i.id === action.payload.platform,
            );
            const type = state.availableTypes.find(
                (i) => i.id === action.payload.type,
            );

            state.orderType = { date, platform, type };

            return state;
        },
        updateTypeRequest: (state, action: PayloadAction<PlatformRequest>) => {
            state.request = {
                ...action.payload,
                date: action.payload.date,
            };

            return state;
        },
        resetSelectTypeState: (state) => {
            state = { ...initialState };
            return state;
        },
        addAvailablePlatform: (state, action: PayloadAction<PlatformResponse>) => {
            state.availablePlatforms = [action.payload];
            state.isFetchingPlatform = false
            return state;
        },
        updateTransferPlatform: (state, action: PayloadAction<PlatformResponse>) => {
            state.selectedPlatform = action.payload
            return state;
        },
    },
    extraReducers: {
        [fetchAvailablePlatformsByDivision.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<PlatformResponse[]>,
        ) => {
            state.availablePlatformsByDivision = payload;
            return state;
        },
    },
});

export const transferTypeSelector = (state: RootState): IOrderTypeSelectorState =>
    state.transferType;

export const { selectType, updateTypeRequest, resetSelectTypeState, addAvailablePlatform, updateTransferPlatform } =
    selectTypeSlice.actions;

export default selectTypeSlice.reducer;
